/**
 * The private chat with the host, as the confirmed prototype draws it in the rooms: the host's face with their
 * online dot, the thread with each side's bubbles, the host typing, and the composer with files (up to five, picked,
 * pasted or dropped). The thread itself is today's (`useWatchThread`): its live wire, its uploads, its typing ping.
 */
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import type { WatchSession } from "../../../shared/types";
import { MessageAttachments } from "../../components/MessageAttachments";
import { ATTACH_ACCEPT, humanSize, type WatchThread } from "../WatchPageView";
import { GIcon } from "./GIcon";
import { mmss } from "./talk";

/** "Just now", "Today, 2:40 PM", "Mon 2:40 PM" this week, "Oct 27" before. */
export function chatWhen(at: number, now: number = Date.now()): string {
  const d = new Date(at * 1000);
  if (now - d.getTime() < 60_000) return "Just now";
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (now - d.getTime() < 6 * 86400_000) return `${d.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The gold "At 12:04" over a line that was written at that second of the talk. */
export function MomentChip({ sec }: { sec: number }): ReactNode {
  return (
    <span className="moment">
      <GIcon name="play" size={12} /> At {mmss(sec)}
    </span>
  );
}

export function RoomChat({
  session,
  thread,
  host,
  onClose,
  sub = "Online now",
  moment,
}: {
  session: WatchSession;
  thread: WatchThread;
  host: string;
  onClose?: () => void;
  sub?: string;
  /** Set beside a recording: the playhead the next message will carry, and the x that leaves it out. */
  moment?: { sec: number; on: boolean; setOn: (on: boolean) => void };
}): ReactNode {
  const log = useRef<HTMLDivElement>(null);
  const [drop, setDrop] = useState(false);
  const { messages, text, queue, trouble, sending, field, picker } = thread;
  const tooMany = queue.length >= 5;

  useEffect(() => {
    const box = log.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages, thread.hostTyping]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    thread.send();
  };

  return (
    <div
      className={drop ? "chat drop" : "chat"}
      data-chat=""
      onDragOver={(e) => {
        e.preventDefault();
        setDrop(true);
      }}
      onDragLeave={() => setDrop(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrop(false);
        if (e.dataTransfer.files.length) thread.pick(e.dataTransfer.files);
      }}
    >
      <div className="chat-h">
        <span className="chat-face">
          {session.host_avatar_url ? <img src={session.host_avatar_url} alt="" /> : <span className="av av-i">{host.charAt(0)}</span>}
          <i />
        </span>
        <div className="chat-who">
          <b>{session.host_name}</b>
          <span>{sub}</span>
        </div>
        {onClose ? (
          <button className="ibtn chat-x" type="button" aria-label="Close the chat" onClick={onClose}>
            <GIcon name="close" />
          </button>
        ) : null}
      </div>
      <div className="chat-log" ref={log}>
        <span className="chat-day">Today</span>
        <p className="chat-priv">
          <GIcon name="lock" size={14} />{" "}
          {moment ? (
            <>
              Just between you and <bdi>{host}</bdi>. If you have left when <bdi>{host}</bdi> answers, it comes to your email too.
            </>
          ) : (
            <>
              Just between you and <bdi>{host}</bdi>. Nobody else sees this chat.
            </>
          )}
        </p>
        {messages.map((m) => {
          const files = m.attachments ?? [];
          const mine = m.sender !== "host";
          return (
            <div className={mine ? "msg mine" : "msg them"} key={m.id}>
              {mine ? null : session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : <span className="av av-i">{host.charAt(0)}</span>}
              <div className="msg-b">
                {m.at_sec === undefined ? null : <MomentChip sec={m.at_sec} />}
                <div className="bub" dir="auto">
                  {m.body}
                  {files.length > 0 ? (
                    <div className="bub-files">
                      <MessageAttachments attachments={files} compact />
                    </div>
                  ) : null}
                </div>
                <span className="msg-meta">
                  {mine ? "You" : host} · {chatWhen(m.created_at)}
                </span>
              </div>
            </div>
          );
        })}
        {thread.hostTyping ? (
          <div className="msg them typing">
            {session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : <span className="av av-i">{host.charAt(0)}</span>}
            <div className="msg-b">
              <div className="bub">
                <i />
                <i />
                <i />
              </div>
              <span className="msg-meta"><bdi>{host}</bdi> is writing</span>
            </div>
          </div>
        ) : null}
      </div>
      <form className="chat-in" onSubmit={submit}>
        {moment?.on ? (
          <div className="chat-ask">
            <MomentChip sec={moment.sec} />
            <span className="chat-ask-w">Your message says where you are in the talk</span>
            <button type="button" className="chat-ask-x" aria-label="Leave the time out" onClick={() => moment.setOn(false)}>
              <GIcon name="close" size={12} stroke={2.4} />
            </button>
          </div>
        ) : null}
        <div className="chat-chips">
          {queue.map((q) => (
            <span className="cchip" key={q.id}>
              <GIcon name="file" size={15} />
              <span>{q.file.name}</span>
              <em>{humanSize(q.file.size)}</em>
              <button type="button" aria-label={`Remove ${q.file.name}`} onClick={() => thread.drop(q.id)}>
                <GIcon name="close" size={14} />
              </button>
            </span>
          ))}
        </div>
        <div className="chat-row">
          <button type="button" className="ibtn" aria-label="Attach a file" disabled={tooMany} onClick={() => picker.current?.click()}>
            <GIcon name="clip" />
          </button>
          <textarea
            ref={field}
            dir="auto"
            rows={1}
            maxLength={2000}
            placeholder={`Write to ${host}`}
            aria-label={`Write to ${host}`}
            value={text}
            onChange={(e) => {
              thread.setText(e.target.value);
              thread.grow();
              thread.noteTyping();
            }}
            onPaste={(e) => {
              if (e.clipboardData.files.length === 0) return;
              e.preventDefault();
              thread.pick(e.clipboardData.files);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              e.preventDefault();
              thread.send();
            }}
          />
          <button className="send" type="submit" aria-label="Send" disabled={sending || (!text.trim() && queue.length === 0)}>
            <GIcon name="send" size={18} stroke={2.2} />
          </button>
          <input ref={picker} type="file" multiple hidden accept={ATTACH_ACCEPT} onChange={(e) => thread.pick(e.target.files)} />
        </div>
        <p className={trouble || tooMany ? "chat-note bad" : "chat-note"}>{trouble || (tooMany ? "Up to 5 files at a time." : "")}</p>
      </form>
    </div>
  );
}
